/**
 * Display and form helpers for race pricing tiers. None of these resolve the
 * active tier or compute prices: the backend owns that. They only convert
 * between wire formats and display strings.
 */

/** Convert 'YYYY-MM-DD HH:mm:ss' (API wall-clock) to 'YYYY-MM-DDTHH:mm' for datetime-local. */
export function apiDatetimeToLocalInput(value: string | null): string {
  if (!value) return ''
  return value.slice(0, 16).replace(' ', 'T')
}

/** Convert a datetime-local value ('YYYY-MM-DDTHH:mm') to 'YYYY-MM-DD HH:mm:ss', or null when empty. */
export function localInputToApiDatetime(value: string): string | null {
  if (!value) return null
  return value.replace('T', ' ') + ':00'
}

/** Price string ('25.00') to integer cents, for display formatting only. */
export function priceToCents(price: string): number {
  return Math.round(Number(price) * 100)
}

/**
 * Format a wall-clock 'YYYY-MM-DD HH:mm[:ss]' string for display without any
 * timezone conversion (the value is already in the race's timezone).
 */
export function formatWallClock(value: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value)
  if (match === null) return value
  const [, year, month, day, hour, minute] = match
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)))
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date)
}

/**
 * Format the date part of an ISO 8601 string with offset ('2027-01-31T23:59:00+01:00')
 * as day + long month, using the date as written (no timezone conversion).
 */
export function formatPriceChangeDate(at: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(at)
  if (match === null) return at
  const [, year, month, day] = match
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date)
}

const PRICE_PATTERN = /^\d{1,8}(\.\d{1,2})?$/

/** Client-side mirror of the server price rule (>= 0, at most 2 decimals). */
export function isValidTierPrice(raw: string): boolean {
  return PRICE_PATTERN.test(raw.trim())
}

let draftCounter = 0

/** Client-only key for an unsaved draft tier. */
export function newDraftTierKey(): string {
  draftCounter += 1
  return `draft-tier-${Date.now()}-${draftCounter}`
}
